import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { PublicLayout } from '../components/landing/PublicLayout';
import { toast } from 'sonner';
import { Mail, MessageSquare, Clock, Send } from 'lucide-react';

export default function ContactPage() {
  const { language } = useAuth();
  const [sending, setSending] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSending(true);
    // Simulated send
    setTimeout(() => {
      setSending(false);
      toast.success(t('contact_success', language));
      e.target.reset();
    }, 1000);
  };

  const subjects = [
    { value: 'general', label: t('contact_subject_general', language) },
    { value: 'bug', label: t('contact_subject_bug', language) },
    { value: 'feature', label: t('contact_subject_feature', language) },
    { value: 'other', label: t('contact_subject_other', language) },
  ];

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      value: t('contact_email_info', language),
      color: 'text-blue-400',
    },
    {
      icon: MessageSquare,
      title: 'Discord',
      value: t('contact_discord_info', language),
      color: 'text-indigo-400',
    },
    {
      icon: Clock,
      title: language === 'fr' ? 'Délai de réponse' : 'Response time',
      value: t('contact_response_time', language),
      color: 'text-green-400',
    },
  ];

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="py-16 sm:py-24 px-4 text-center relative">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-background to-background dark:from-primary/8" />
        <div className="relative z-10 max-w-3xl mx-auto">
          <h1 className="font-cinzel text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            {t('contact_page_title', language)}
          </h1>
          <p className="text-muted-foreground text-base sm:text-lg">
            {t('contact_page_subtitle', language)}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-20 sm:pb-28 px-4">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Form */}
          <div className="lg:col-span-3">
            <Card className="border-border/50">
              <CardContent className="p-6">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">{t('contact_name_label', language)}</Label>
                      <Input id="name" required placeholder={language === 'fr' ? 'Votre nom' : 'Your name'} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">{t('contact_email_label', language)}</Label>
                      <Input id="email" type="email" required placeholder={language === 'fr' ? 'votre@email.com' : 'your@email.com'} />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject">{t('contact_subject_label', language)}</Label>
                    <select
                      id="subject"
                      required
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <option value="">{language === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                      {subjects.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">{t('contact_message_label', language)}</Label>
                    <Textarea
                      id="message"
                      required
                      rows={6}
                      placeholder={language === 'fr' ? 'Décrivez votre demande...' : 'Describe your request...'}
                    />
                  </div>

                  <Button
                    type="submit"
                    className="fantasy-button w-full sm:w-auto px-8"
                    disabled={sending}
                  >
                    {sending ? (
                      <span className="animate-pulse">{language === 'fr' ? 'Envoi...' : 'Sending...'}</span>
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        {t('contact_submit', language)}
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact info */}
          <div className="lg:col-span-2 space-y-4">
            <h2 className="font-cinzel font-bold text-lg mb-4">
              {t('contact_info_title', language)}
            </h2>

            {contactInfo.map((info, i) => {
              const Icon = info.icon;
              return (
                <Card key={i} className="border-border/50">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className={`p-2.5 rounded-xl bg-card border border-border/50 ${info.color}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="text-sm font-medium">{info.title}</div>
                      <div className="text-sm text-muted-foreground">{info.value}</div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
